// =========================================================
// Vojjin Hash Utilities
// =========================================================
import CryptoJS from "crypto-js";

;(function (global) {
    // Create namespace
    const G = global.gemini || (global.gemini = {});
    G.hash = {};

    // Sync CryptoJS wrappers
    /**
     * Computes the MD5 hash of the given input.
     *
     * This method takes an input string or binary data and computes
     * the message digest using the MD5 hashing algorithm. The resulting
     * hash is typically represented as a 32-character hexadecimal string.
     *
     * Note that MD5 is considered cryptographically insecure for security-sensitive
     * applications. It is recommended to use more secure hashing algorithms
     * such as SHA-256 for cryptographic purposes.
     *
     * @param {string|Buffer} input - The input data to hash. Can be a string or a Buffer object.
     * @returns {string} The computed MD5 hash as a hexadecimal string.
     */
    G.hash.md5 = function (input) {
        return CryptoJS.MD5(input).toString(CryptoJS.enc.Hex);
    };

    /**
     * Provides functionality for generating a SHA-1 hash.
     *
     * The `G.hash.sha1` function allows creating a secure hash using the
     * SHA-1 (Secure Hash Algorithm 1) cryptographic hash function. SHA-1
     * produces a 160-bit (20-byte) hash value, typically rendered as a
     * hexadecimal number that is 40 characters long.
     *
     * Note: SHA-1 is considered cryptographically weak and should not
     * be used for secure applications to protect sensitive information.
     * Consider using a more secure hashing algorithm like SHA-256 for
     * such use cases.
     @param {string|Buffer} input - The input data to hash. Can be a string or a Buffer object.
     @returns {string} The computed MD5 hash as a hexadecimal string.
     */
    G.hash.sha1 = function (input) {
        return CryptoJS.SHA1(input).toString(CryptoJS.enc.Hex);
    };

    /**
     * Computes the SHA-256 cryptographic hash of the given input.
     *
     * This function is part of the `G.hash` namespace and provides a way
     * to generate a secure hash value using the SHA-256 algorithm.
     *
     * @function sha256
     * @memberof G.hash
     * @param {string|Buffer} input - The data to hash. It can be provided as a string or a Buffer object.
     * @returns {string} The resulting hash represented as a hexadecimal string.
     * @throws {Error} If the input is invalid or hashing fails.
     */
    G.hash.sha256 = function (input) {
        return CryptoJS.SHA256(msg).toString(CryptoJS.enc.Hex);
    };

    async function subtleDigest(algorithm, message) {
        const msgBuffer = new TextEncoder().encode(message);
        const hashBuffer = await crypto.subtle.digest(algorithm, msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    }

    G.hash.sha1Async = msg => subtleDigest("SHA-1", msg);
    G.hash.sha256Async = msg => subtleDigest("SHA-256", msg);
    G.hash.sha512Async = msg => subtleDigest("SHA-512", msg);

    /**
     * Encodes the given input to a Base64-encoded string.
     *
     * @function
     * @param {string} input - The string to be encoded.
     * @returns {string} The Base64-encoded representation of the input string.
     * @throws {TypeError} If the input is not a string.
     */
    G.hash.base64Encode = function (input) {
        try {
            const utf8 = new TextEncoder().encode(input);
            let binary = "";
            utf8.forEach(b => (binary += String.fromCharCode(b)));
            return btoa(binary);
        } catch (err) {
            console.error("Base64 encode failed:", err);
            return null;
        }
    };

    /**
     * Decodes a Base64-encoded string and returns the corresponding decoded value.
     * This function is commonly used to reverse the Base64 encoding applied to a string,
     * allowing the original string to be retrieved.
     *
     * @function
     * @name G.hash.base64Decode
     * @param {string} encodedString - The Base64-encoded string to decode.
     * @returns {string} The decoded string.
     * @throws {Error} If the input is not a valid Base64-encoded string.
     */
    G.hash.base64Decode = function (encodedString) {
        try {
            const binary = atob(encodedString);
            const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
            return new TextDecoder().decode(bytes);
        } catch (err) {
            console.error("Base64 decode failed:", err);
            return null;
        }
    };

    if (typeof module !== "undefined" && module.exports) {
        module.exports = G.hash;
    } else {
        global.gemini = G;
        global.gemini.hash = G.hash;
    }
})(typeof globalThis !== "undefined" ? globalThis : this);
